/**
 * User Management Page
 * Admin-only page for managing users assigned to TotalSERP
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Security as SecurityIcon, 
  Refresh as RefreshIcon, 
  PersonAdd as PersonAddIcon, 
  Search as SearchIcon, 
  Delete as DeleteIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useToast } from '../context/ToastContext';
import { 
  getAuthGuardResourceUsers, 
  deleteProfile, 
  AuthGuardUserProfile 
} from '../services/usersService';
import AddUserDialog from '../components/AddUserDialog';
import UserAccessControlModal from '../components/UserAccessControlModal';
import { useAuth } from '../context/AuthContext';

interface UserRowProps {
  user: AuthGuardUserProfile;
  onDeleteUser: (user: AuthGuardUserProfile) => void;
  onManageAccess: (user: AuthGuardUserProfile) => void;
}

const UserRow = React.memo(({ user, onDeleteUser, onManageAccess }: UserRowProps) => {
  return (
    <TableRow 
      hover
      sx={{
        '&:hover .action-icon': {
          visibility: 'visible'
        }
      }}
    >
      <TableCell>
        {user.personalInformation ? (
          `${user.personalInformation.firstName} ${user.personalInformation.lastName}`
        ) : (
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {user.personalInformation?.email || (
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {user.profileInformation.profileName}
        </Typography>
      </TableCell>
      <TableCell>{user.globalSettings.country}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
          {user.globalSettings.timeZone}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Manage client access">
            <IconButton
              className="action-icon"
              size="small"
              onClick={() => onManageAccess(user)}
              sx={{ 
                visibility: 'hidden',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.dark',
                }
              }}
            >
              <ShieldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete user">
            <IconButton
              className="action-icon"
              size="small"
              onClick={() => onDeleteUser(user)}
              sx={{ 
                visibility: 'hidden',
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.dark',
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

UserRow.displayName = 'UserRow';

export default function UserManagement() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  
  const [users, setUsers] = useState<AuthGuardUserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<'name' | 'email'>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AuthGuardUserProfile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [accessControlModalOpen, setAccessControlModalOpen] = useState(false);
  const [accessControlUser, setAccessControlUser] = useState<AuthGuardUserProfile | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch AuthGuard users for the 'serp' resource
      const authGuardResponse = await getAuthGuardResourceUsers('serp', true);

      // Deduplicate users by profileId to prevent duplicate entries
      const uniqueUsers = Array.from(
        new Map(authGuardResponse.users.map(user => [user.profileInformation.profileId, user])).values()
      );

      setUsers(uniqueUsers);
    } catch (error: any) {
      console.error('Error loading users:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load users';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Handle sorting
  const handleRequestSort = (property: 'name' | 'email') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter users by search term
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const name = `${user.personalInformation?.firstName || ''} ${user.personalInformation?.lastName || ''}`.toLowerCase();
      const email = user.personalInformation?.email?.toLowerCase() || '';
      const profile = user.profileInformation.profileName?.toLowerCase() || '';
      
      return name.includes(searchLower) || email.includes(searchLower) || profile.includes(searchLower);
    });
  }, [users, searchTerm]);

  // Sort users
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let comparison = 0;
      
      if (orderBy === 'name') {
        const nameA = `${a.personalInformation?.firstName || ''} ${a.personalInformation?.lastName || ''}`.toLowerCase();
        const nameB = `${b.personalInformation?.firstName || ''} ${b.personalInformation?.lastName || ''}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (orderBy === 'email') {
        const emailA = a.personalInformation?.email?.toLowerCase() || '';
        const emailB = b.personalInformation?.email?.toLowerCase() || '';
        comparison = emailA.localeCompare(emailB);
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
  }, [filteredUsers, orderBy, order]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    return sortedUsers.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedUsers, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUsersAdded = () => {
    // Reload the user list after adding new users
    loadUsers();
  };

  // Get existing user emails for the AddUserDialog
  const existingUserEmails = users
    .map(u => u.personalInformation?.email?.toLowerCase().trim())
    .filter(Boolean) as string[];

  const handleManageAccess = useCallback((user: AuthGuardUserProfile) => {
    setAccessControlUser(user);
    setAccessControlModalOpen(true);
  }, []);

  const handleCloseAccessControl = useCallback(() => {
    setAccessControlModalOpen(false);
    setAccessControlUser(null);
  }, []);

  const handleDeleteUser = useCallback((user: AuthGuardUserProfile) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;

    const profileId = userToDelete.profileInformation.profileId;
    const userName = userToDelete.personalInformation 
      ? `${userToDelete.personalInformation.firstName} ${userToDelete.personalInformation.lastName}`
      : profileId;

    setDeleting(true);

    try {
      // Delete the profile from the 'serp' resource
      await deleteProfile(profileId, 'serp');
      showToast(`Successfully deleted user ${userName}`, 'success');
      
      // Close dialog and reload users
      handleCloseDeleteDialog();
      loadUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user';
      showToast(`Failed to delete user: ${errorMessage}`, 'error');
    } finally {
      setDeleting(false);
    }
  }, [userToDelete, showToast, handleCloseDeleteDialog]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon fontSize="large" />
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Manage users and roles for TotalSERP
        </Typography>

        {!isAdmin && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You do not have admin permissions to manage users.
          </Alert>
        )}

        <Paper elevation={2}>
          <Box sx={{ pl: 2, pr: 0.25, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              TotalSERP Users {!loading && `(${filteredUsers.length}${searchTerm ? ` of ${users.length}` : ''})`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddUserDialogOpen(true)}
                disabled={loading || !isAdmin}
              >
                Add Users
              </Button>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0); // Reset to first page on search
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Refresh users">
                        <IconButton 
                          onClick={loadUsers} 
                          disabled={loading}
                          size="small"
                          edge="end"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ 
                  border: 'none',
                  '& .MuiTablePagination-toolbar': {
                    paddingLeft: 1,
                    paddingRight: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                    minHeight: '40px',
                  }
                }}
              />
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Profile</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Time Zone</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedUsers.map((user) => (
                      <UserRow 
                        key={user.profileInformation.profileId}
                        user={user}
                        onDeleteUser={handleDeleteUser}
                        onManageAccess={handleManageAccess}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && filteredUsers.length > 0 && (
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          )}
        </Paper>

        <AddUserDialog
          open={addUserDialogOpen}
          onClose={() => setAddUserDialogOpen(false)}
          existingUserEmails={existingUserEmails}
          onUsersAdded={handleUsersAdded}
        />

        {/* User Access Control Modal */}
        {accessControlUser && (
          <UserAccessControlModal
            open={accessControlModalOpen}
            onClose={handleCloseAccessControl}
            userEmail={accessControlUser.personalInformation?.email || ''}
            userName={
              accessControlUser.personalInformation
                ? `${accessControlUser.personalInformation.firstName} ${accessControlUser.personalInformation.lastName}`
                : accessControlUser.profileInformation.profileName
            }
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete user{' '}
              <strong>
                {userToDelete?.personalInformation
                  ? `${userToDelete.personalInformation.firstName} ${userToDelete.personalInformation.lastName}`
                  : userToDelete?.profileInformation.profileId}
              </strong>
              ?
              <br />
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
